/**
 * Project Activity Service — Projects v2 Phase 2 (P2.2).
 *
 * Sub-collection infrastructure for `projects/{projectId}/activity/{entryId}`.
 * Every material change in Phase 2+ writes here; the last 20 are mirrored
 * into `Project.recentActivity[]` for list-card perf — dual-write pattern
 * per master plan §1.2.
 *
 * Used by:
 *   - project-workflow.service.ts (P2.3 — stage transitions)
 *   - project-variation.service.ts (Phase 7 — variation approvals)
 *   - auto-action-handlers.ts (functions/, P2.5 — server-side handler
 *     completions). Note: the server handlers use the firebase-admin SDK
 *     directly; this client-side service is NOT shared with them. The pure
 *     sanitize logic below COULD be shared later if needed.
 *
 * Tenant scoping: every query filters by `tenantId` from localStorage with
 * the canonical 'techery-au' fallback per CLAUDE.md.
 */

import { getTenantIdSync } from '@/lib/tenant';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  startAfter,
  where,
  Timestamp,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { stripUndefinedDeep } from '@/utils/firestore';
import type { ProjectActivityEntry } from '@/types/project.types';

const RECENT_ACTIVITY_MIRROR_LIMIT = 20;
const DEFAULT_PAGE_LIMIT = 50;

function readTenantId(): string {
  if (typeof window === 'undefined') return 'techery-au';
  return getTenantIdSync();
}

/** Convert any timestamp-shaped value to an ISO string; mirrors the
 *  sanitizeFault helper at src/hooks/useFaults.ts:27. */
function toISOString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value && typeof value === 'object' && 'seconds' in (value as Record<string, unknown>)) {
    return new Date((value as { seconds: number }).seconds * 1000).toISOString();
  }
  return new Date().toISOString();
}

/** Convert any Firestore Timestamp on an activity doc to an ISO string.
 *  Entries should already carry ISO strings (we write them that way), but
 *  legacy docs or migrations could leave Timestamps behind — same defensive
 *  pattern as sanitizeFault. */
function sanitizeActivityEntry(raw: Record<string, unknown>): ProjectActivityEntry {
  const sanitized = { ...raw };
  if (sanitized.timestamp && typeof sanitized.timestamp !== 'string') {
    sanitized.timestamp = toISOString(sanitized.timestamp);
  }
  return sanitized as unknown as ProjectActivityEntry;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Append an activity entry atomically: write to the sub-collection, then
 * update the parent Project.recentActivity[] mirror (newest first, capped at
 * 20). Uses runTransaction so either both writes succeed or neither do.
 *
 * Returns the new entry's Firestore doc ID.
 */
export async function appendActivityEntry(
  projectId: string,
  entry: Omit<ProjectActivityEntry, 'id' | 'timestamp'>
): Promise<string> {
  const tenantId = readTenantId();
  const entryId = crypto.randomUUID();
  const timestamp = new Date().toISOString();

  const fullEntry: ProjectActivityEntry = {
    ...entry,
    id: entryId,
    timestamp,
  };

  const projectRef = doc(db, 'projects', projectId);
  const activityRef = doc(db, 'projects', projectId, 'activity', entryId);

  await runTransaction(db, async (tx) => {
    const projectSnap = await tx.get(projectRef);
    if (!projectSnap.exists()) {
      throw new Error(`Project ${projectId} not found`);
    }
    const projectData = projectSnap.data();
    const projectTenant = (projectData.tenantId ?? 'techery-au') as string;
    if (projectTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch — project is "${projectTenant}", caller is "${tenantId}"`
      );
    }

    // Prepend the new entry, truncate to the mirror cap. Callers commonly
    // pass `details: undefined` from ternaries like `x ? '...' : undefined`,
    // which Firestore rejects in nested writes — strip undefined deep on
    // both the activity doc AND the mirror so neither path explodes.
    const existingMirror = Array.isArray(projectData.recentActivity)
      ? (projectData.recentActivity as ProjectActivityEntry[])
      : [];
    const nextMirror = stripUndefinedDeep(
      [fullEntry, ...existingMirror].slice(0, RECENT_ACTIVITY_MIRROR_LIMIT)
    );

    // Sub-collection write carries tenantId so per-collection queries can
    // filter without re-reading the parent.
    tx.set(
      activityRef,
      stripUndefinedDeep({
        ...fullEntry,
        tenantId,
        projectId,
      })
    );
    tx.update(projectRef, {
      recentActivity: nextMirror,
      updatedAt: timestamp,
    });
  });

  return entryId;
}

// ---------------------------------------------------------------------------
// Read — one-shot pagination
// ---------------------------------------------------------------------------

export interface ListActivityOpts {
  limit?: number;
  /** ISO timestamp of the last entry already shown — fetch entries older
   *  than this. Matches how `useProjectActivity.loadMore` paginates. */
  startAfter?: string;
  /** Restrict to a single activity type. */
  type?: ProjectActivityEntry['type'];
}

/** Paginated one-shot fetch of activity entries for a project, newest first. */
export async function listActivity(
  projectId: string,
  opts: ListActivityOpts = {}
): Promise<ProjectActivityEntry[]> {
  const tenantId = readTenantId();
  const {
    limit = DEFAULT_PAGE_LIMIT,
    startAfter: startAfterTimestamp,
    type,
  } = opts;

  const constraints: QueryConstraint[] = [
    where('tenantId', '==', tenantId),
    orderBy('timestamp', 'desc'),
    firestoreLimit(limit),
  ];
  if (type) constraints.splice(1, 0, where('type', '==', type));
  if (startAfterTimestamp) constraints.push(startAfter(startAfterTimestamp));

  const q = query(collection(db, 'projects', projectId, 'activity'), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d: QueryDocumentSnapshot<DocumentData>) =>
    sanitizeActivityEntry({ id: d.id, ...d.data() })
  );
}

// ---------------------------------------------------------------------------
// Read — real-time subscription
// ---------------------------------------------------------------------------

export interface SubscribeActivityOpts {
  limit?: number;
}

/** Real-time listener for the most recent activity entries. Returns an
 *  unsubscribe function. */
export function subscribeActivity(
  projectId: string,
  cb: (entries: ProjectActivityEntry[]) => void,
  opts: SubscribeActivityOpts = {}
): Unsubscribe {
  const tenantId = readTenantId();
  const { limit = DEFAULT_PAGE_LIMIT } = opts;

  const q = query(
    collection(db, 'projects', projectId, 'activity'),
    where('tenantId', '==', tenantId),
    orderBy('timestamp', 'desc'),
    firestoreLimit(limit)
  );

  return onSnapshot(
    q,
    (snap) => {
      const entries = snap.docs.map((d) =>
        sanitizeActivityEntry({ id: d.id, ...d.data() })
      );
      cb(entries);
    },
    (err) => {
      console.warn(`[project-activity] subscribe error on project ${projectId}:`, err);
      cb([]);
    }
  );
}

// ---------------------------------------------------------------------------
// One-shot single-entry read (mostly for tests / admin tooling)
// ---------------------------------------------------------------------------

export async function getActivityEntry(
  projectId: string,
  entryId: string
): Promise<ProjectActivityEntry | null> {
  const ref = doc(db, 'projects', projectId, 'activity', entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return sanitizeActivityEntry({ id: snap.id, ...snap.data() });
}

// Intentionally no generic "update" or "delete" — activity entries are the
// audit trail. Phase 8 may add scoped edits for mention-rewrite / redaction;
// out of scope here.
